import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `result`, in the terminal register — a status glyph, the summary, and the
 * verbatim evidence under a continuation rule.
 *
 * The measured shape this serves: of 409 result records, 110 fail, and every
 * failure is already multi-line text that reads like a throw — the offending
 * line, then the error. So the summary and its evidence are two different
 * things and get two different registers, rather than one truncated grey line
 * that shows neither.
 *
 * `ok` is carried by the contract and drawn by nobody: the composition folds
 * a successful turn into the bar. Rendering it here would put a badge on all
 * 409, which is how a reader stops seeing the 110. It is handled anyway
 * rather than thrown on, because a slot that crashes on a legal value is a
 * worse failure than one that draws a quiet line. */
@component({ name: "std-term-turn-result" })
export class TermTurnResult extends LonaWebComponent {
  private $mark: HTMLElement = this.$("mark");
  private $text: HTMLElement = this.$("text");
  private $detail: HTMLElement = this.$("detail");

  static readonly MARK: Record<TurnSlots.ResultStatus, string> = {
    ok: "✓",
    error: "✕",
    suspended: "⏸",
  };

  static readonly TONE: Record<TurnSlots.ResultStatus, string> = {
    ok: "var(--fg-green, #2da44e)",
    error: "var(--fg-red, #e5484d)",
    suspended: "var(--fg-yellow, #d4a72c)",
  };

  static makeWith(props: TurnSlots.Props["result"]): TermTurnResult {
    return (TermTurnResult.make() as TermTurnResult).bind(props);
  }

  bind(props: TurnSlots.Props["result"]): this {
    this.$mark.textContent = TermTurnResult.MARK[props.status];
    this.$mark.style.color = TermTurnResult.TONE[props.status];
    this.$text.textContent = props.text;
    const detail = props.detail ?? "";
    this.$detail.textContent = detail;
    this.$detail.style.display = detail === "" ? "none" : "";
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-pri, #1f2328);
      }
      #row {
        display: flex;
        align-items: baseline;
        gap: 1ch;
      }
      #mark {
        flex: none;
        width: 1ch;
        text-align: center;
      }
      #text {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      #detail {
        margin: 2px 0 0 0;
        padding-left: 2ch;
        margin-left: 0.5ch;
        border-left: 1px solid var(--divider, #d8dee9);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        color: var(--text-sec, #5c6370);
        max-height: 18em;
        overflow-y: auto;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="row"><span id="mark"></span><span id="text"></span></div>
    <pre id="detail"></pre>
  `;
}
